import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, Siren, ArrowRight, CheckCircle2 } from "lucide-react";
import { CONTACT, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    address: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone, and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received! Schuyler will reach out shortly.");
      setForm({ name: "", email: "", phone: "", service: "", address: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#141414] border border-[#374151] text-white placeholder:text-white/40 px-4 py-3 focus:outline-none focus:border-[#D86C1E] focus:ring-2 focus:ring-[#D86C1E]/40 transition-all";

  return (
    <section id="quote" data-testid="quote-section" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left info */}
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D86C1E] mb-4">
              Free, No-Pressure Estimate
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-tight text-white">
              Request Your Free Roof Quote
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/60">
              Tell Schuyler a little about your project and he'll get back to you personally —
              usually the same day. No call centers. No obligation. Just straight answers.
            </p>

            <div className="mt-10 space-y-5">
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-4 group">
                <div className="flex h-12 w-12 items-center justify-center bg-[#D86C1E]">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/40">Call or Text</div>
                  <div className="font-heading font-bold text-white group-hover:text-[#D86C1E] transition-colors">
                    {CONTACT.phone}
                  </div>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group">
                <div className="flex h-12 w-12 items-center justify-center bg-[#D86C1E]">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/40">Email</div>
                  <div className="font-heading font-bold text-white group-hover:text-[#D86C1E] transition-colors break-all">
                    {CONTACT.email}
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center bg-[#D86C1E]">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/40">Standard Hours</div>
                  <div className="font-heading font-bold text-white">{CONTACT.hours}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center bg-white/10">
                  <Siren className="h-5 w-5 text-[#D86C1E]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/40">Emergencies</div>
                  <div className="font-heading font-bold text-white">{CONTACT.emergency}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-[#1A1A1A] border border-[#374151] p-8 md:p-10">
            {done ? (
              <div
                data-testid="quote-success"
                className="h-full flex flex-col items-center justify-center text-center py-12"
              >
                <CheckCircle2 className="h-16 w-16 text-[#D86C1E] mb-6" />
                <h3 className="font-heading text-2xl font-black text-white mb-3">
                  Request Received!
                </h3>
                <p className="text-white/60 max-w-sm">
                  Thanks for reaching out. Schuyler will contact you shortly to schedule your free
                  inspection.
                </p>
                <button
                  data-testid="quote-reset-btn"
                  onClick={() => setDone(false)}
                  className="mt-8 text-[#D86C1E] font-heading font-bold uppercase tracking-wide text-sm"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form data-testid="quote-request-form" onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    data-testid="quote-name"
                    className={inputCls}
                    placeholder="Full Name *"
                    value={form.name}
                    onChange={update("name")}
                  />
                  <input
                    data-testid="quote-phone"
                    className={inputCls}
                    placeholder="Phone *"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>
                <input
                  data-testid="quote-email"
                  type="email"
                  className={inputCls}
                  placeholder="Email *"
                  value={form.email}
                  onChange={update("email")}
                />
                <select
                  data-testid="quote-service"
                  className={inputCls}
                  value={form.service}
                  onChange={update("service")}
                >
                  <option value="">Select a Service *</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                  <option value="Other / Not Sure">Other / Not Sure</option>
                </select>
                <input
                  data-testid="quote-address"
                  className={inputCls}
                  placeholder="Property Address / City"
                  value={form.address}
                  onChange={update("address")}
                />
                <textarea
                  data-testid="quote-message"
                  rows={4}
                  className={inputCls}
                  placeholder="Tell us about your roof..."
                  value={form.message}
                  onChange={update("message")}
                />
                <button
                  type="submit"
                  data-testid="quote-submit-btn"
                  disabled={loading}
                  className="group w-full inline-flex items-center justify-center gap-2 bg-[#D86C1E] hover:bg-[#B0551A] disabled:opacity-60 text-white font-heading font-bold text-base uppercase tracking-wide px-8 py-4 transition-all hover:-translate-y-0.5"
                >
                  {loading ? "Sending..." : "Get My Free Quote"}
                  {!loading && (
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </button>
                <p className="text-xs text-white/40 text-center">
                  By submitting, you agree to be contacted about your roofing project.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
